import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getPricingString } from 'common/utils/common';
import { Typography, TextField, InputAdornment, Box } from '@mui/material';
import { CurrencyObject, Languages, _PaymentObject } from 'common/types';
import {
	getCurrencyObjectFromCurrencyCode,
	isDisplayAsSymbol,
	getCurrencySymbolFromCurrencyCode,
} from 'common/utils/currencyUtils';
import ActionDialog from 'common/components/ActionDialog';
import { CapturePaymentProps } from '../PaymentsList/utils';
import { useTranslation } from 'services/localization/useTranslation';
import { PaymentProvider } from 'common/modules/payments/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getEmailTypeDetails } from 'views/Store/Tabs/Emails/utils';
import { CUSTOMER_FACING_EMAILS } from 'common/modules/emails/constants';

interface Props {
	userEmail: string;
	userLanguage: Languages | undefined;
	paymentObject: _PaymentObject;
	shopCurrency: CurrencyObject;
	onCancel: () => void;
	onCharge: (data: CapturePaymentProps) => void;
	loading: boolean;
	error: string;
	provider: PaymentProvider;
}

const CaptureTransactionDialog = (props: Props) => {
	const {
		shopCurrency,
		paymentObject,
		loading,
		onCancel,
		onCharge,
		provider,
		userLanguage,
	} = props;
	const currency = paymentObject.currency;
	const depositAmount = paymentObject.authorised || 0;
	const [chargeAmount, setChargeAmount] = useState<number | undefined>(
		paymentObject.authorised || 0,
	);
	const [userEmail, setUserEmail] = useState(props.userEmail);
	const [captureDescription, setCaptureDescription] = useState('');
	const [shopMessage, setShopMessage] = useState('');
	const [error, setError] = useState<string | undefined>();

	const locationEmailSettings = useSelector(ShopSelectors.activeLocationEmailSettings);
	const isChargeDepositEmailEnabled =
		locationEmailSettings?.emails?.depositCaptured?.enabled ?? true;

	const { t } = useTranslation();

	const handleChargeAmountChange = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		let value = parseFloat(event.target.value) * 100;
		let chargeValue: number | undefined;
		if (!isNaN(value)) {
			chargeValue = value;
		}
		if (chargeValue && chargeValue > depositAmount) {
			setError(t('captureDialog.amountTooHigh', 'Amount is too high'));
		} else {
			setError(undefined);
		}
		setChargeAmount(chargeValue);
	};

	const handleChargeClick = () => {
		const result: CapturePaymentProps = {
			amount: {
				value: chargeAmount ?? 0,
				currency,
			},
			captureDescription,
			shopMessage,
			userEmail,
			language: userLanguage,
		};
		onCharge(result);
	};

	return (
		<ActionDialog
			title={t('captureDialog.title', 'Charge deposit')}
			btnActions={{
				primary: {
					text: t('common:actions.charge', 'Charge'),
					loading,
					disabled: loading || !chargeAmount || chargeAmount <= 0 || chargeAmount > depositAmount,
					onClick: handleChargeClick,
				},
				secondary: {
					text: t('common:actions.cancel'),
					disabled: loading,
					onClick: onCancel,
				},
			}}
			onClose={onCancel}
		>
			<>
				<Typography variant="body2" gutterBottom>
					{t('captureDialog.amountReserved', 'Reserved amount')}:{' '}
					{getPricingString(
						depositAmount,
						getCurrencyObjectFromCurrencyCode(currency, shopCurrency),
					)}
				</Typography>
				<Box pb={1} />
				<TextField
					variant="standard"
					label={t('captureDialog.totalToCharge', 'Amount to charge')}
					value={chargeAmount || chargeAmount === 0 ? chargeAmount / 100 : ''}
					onChange={handleChargeAmountChange}
					type="number"
					InputProps={{
						endAdornment: isDisplayAsSymbol(shopCurrency) ? (
							<InputAdornment position="end">
								{getCurrencySymbolFromCurrencyCode(currency)}
							</InputAdornment>
						) : null,
					}}
					margin="dense"
					fullWidth
					helperText={
						chargeAmount && chargeAmount < depositAmount
							? t(
									'captureDialog.partialChargeNote',
									'If you charge only part of the amount, the remaining amount will be automatically canceled',
							  )
							: ''
					}
				/>
				<TextField
					variant="standard"
					label={t('captureDialog.chargeReason', 'Charge reason')}
					value={captureDescription}
					onChange={(e) =>
						e.target.value.length > 30 ? undefined : setCaptureDescription(e.target.value)
					}
					type="text"
					margin="dense"
					fullWidth
				/>
				<Box pt={2} />
				<Box pt={3} />
				{provider === 'ADYEN' ? (
					<>
						<Typography variant="subtitle2" gutterBottom>
							{t('captureDialog.messageTitle', 'Message sent with the charge')}
						</Typography>
						<TextField
							variant="standard"
							label={t('common:form.email')}
							value={userEmail}
							disabled={!isChargeDepositEmailEnabled}
							onChange={(e) => setUserEmail(e.target.value)}
							type="text"
							fullWidth
							margin="dense"
						/>
						<TextField
							variant="standard"
							label={t('captureDialog.messageToCustomer', 'Shop message to the customer')}
							value={shopMessage}
							disabled={!isChargeDepositEmailEnabled}
							onChange={(e) => setShopMessage(e.target.value)}
							type="text"
							fullWidth
							margin="dense"
						/>
						<Box pt={2} />
						<Typography color="textSecondary" variant="body2">
							{isChargeDepositEmailEnabled
								? t(
										'captureDialog.messageNote',
										'The email will contain the receipt for the charge, and optional shop message.',
								  )
								: t('emails.emailDisabledDisclaimer', {
										emailName: getEmailTypeDetails(t, CUSTOMER_FACING_EMAILS.depositCaptured)
											.heading,
										defaultValue: 'The {{emailName}} email is disabled in Email settings',
								  })}
						</Typography>
					</>
				) : (
					<Typography variant="body2">
						{t(
							'captureDialog.manualNote',
							'Note: The deposit was reserved manually - no automatic charge from customers card will be done',
						)}
					</Typography>
				)}
				<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
					{error}
				</Typography>
			</>
		</ActionDialog>
	);
};

export default CaptureTransactionDialog;
